import { normalizePhone } from '../utils/phoneUtils';

const API_URL = "https://api.whatsapp.com/send";

/**
 * Build the WhatsApp send URL.
 *
 * Extracted so flows that need to open the tab BEFORE knowing the final message
 * (e.g. batch creation of recurring appointments) can reuse the exact same URL
 * format. Browsers only allow window.open in direct response to a user gesture,
 * so those flows open a blank tab on click and redirect it later.
 *
 * @param {string} phone - Phone number (DDD + number, e.g. "11994686881")
 * @param {string} message - Message to send
 * @param {string} countryCode - Country code / DDI (e.g. "55" for Brazil)
 * @returns {string|null} URL or null when the phone is invalid
 */
const BuildWhatsAppUrl = (phone, message, countryCode = '') =>
{
    const finalPhone = normalizePhone(phone, countryCode);

    if (!finalPhone) {
        return null;
    }

    return API_URL + '?phone=' + finalPhone + '&text=' + message;
}

/**
 * Open WhatsApp Web/App to send a message.
 * 
 * @param {string} phone - Phone number (DDD + number, e.g. "11994686881")
 * @param {string} message - Message to send
 * @param {string} countryCode - Country code / DDI (e.g. "55" for Brazil)
 */
const SendWhatsAppMessage = (phone, message, countryCode = '') =>
{
    const url = BuildWhatsAppUrl(phone, message, countryCode);

    if (!url) return;

    window.open(url, '_blank', 'noreferrer');
}

/**
 * Redirect an ALREADY OPEN window/tab to WhatsApp.
 *
 * Used when the message content is only known after an async response: the tab
 * is opened blank during the click (allowed by the popup blocker) and then
 * redirected here.
 *
 * @param {Window|null} targetWindow - Window returned by window.open('', '_blank')
 * @param {string} phone
 * @param {string} message
 * @param {string} countryCode
 * @returns {boolean} true when the redirect happened
 */
const SendWhatsAppMessageToWindow = (targetWindow, phone, message, countryCode = '') =>
{
    if (!targetWindow || targetWindow.closed) return false;

    const url = BuildWhatsAppUrl(phone, message, countryCode);

    if (!url) {
        targetWindow.close();
        return false;
    }

    targetWindow.location.href = url;
    return true;
}

export {SendWhatsAppMessage, SendWhatsAppMessageToWindow, BuildWhatsAppUrl};
